import { scopeProjectRef } from "@t3tools/client-runtime/environment";
import { useAtomValue } from "@effect/atom-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { LinkIcon, PlusIcon, RotateCcwIcon } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import ChatView from "../components/ChatView";
import { MorrowAuthWelcome } from "../components/clerk/MorrowAuthWelcome";
import { shouldShowAuthWelcome } from "../components/clerk/MorrowAuthWelcome.logic";
import { useMorrowClerkAuth } from "../components/clerk/morrowClerkAuth";
import { sortScopedProjectsForSidebar } from "../components/Sidebar.logic";
import { useComposerDraftStore } from "../composerDraftStore";
import { Button } from "../components/ui/button";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "../components/ui/empty";
import { SidebarInset } from "../components/ui/sidebar";
import { useNewThreadHandler } from "../hooks/useHandleNewThread";
import { newDraftId, newProjectId, newThreadId } from "../lib/utils";
import {
  useAllEnvironmentShellsBootstrapped,
  useProjects,
  useThreadShells,
} from "../state/entities";
import { useEnvironments } from "../state/environments";
import { primaryEnvironmentIdAtom } from "../state/primaryEnvironment";
import { APP_DISPLAY_NAME } from "~/branding";
import { hasClerkPublishableKey, hasCloudPublicConfig } from "~/cloud/publicConfig";
import { cn } from "~/lib/utils";
import { COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS } from "~/workspaceTitlebar";

function ChatIndexRouteView() {
  const { authGateState } = Route.useRouteContext();
  const { environments } = useEnvironments();

  if (authGateState.status === "hosted-static" && environments.length === 0) {
    return <HostedStaticOnboardingState />;
  }

  return <IndexDraftLanding />;
}

/**
 * Landing on the index route drops straight into a draft thread for the most
 * recently active project, so the first screen is a prompt instead of a dead
 * end. Falls back to an add-project hero when no project exists yet.
 */
function IndexDraftLanding() {
  const projects = useProjects();
  const threads = useThreadShells();
  const bootstrapped = useAllEnvironmentShellsBootstrapped();
  const handleNewThread = useNewThreadHandler();
  const startingRef = useRef(false);
  const [startState, setStartState] = useState({ failed: false, retryRequest: 0 });
  const { isLoaded, isSignedIn } = useMorrowClerkAuth();
  const showAuthWelcome = shouldShowAuthWelcome({
    isDraftHero: true,
    clerkEnabled: hasClerkPublishableKey(),
    isLoaded,
    isSignedIn,
  });

  const mostRecentProject = useMemo(
    () =>
      bootstrapped
        ? (sortScopedProjectsForSidebar(projects, threads, "updated_at")[0] ?? null)
        : null,
    [bootstrapped, projects, threads],
  );

  useEffect(() => {
    // Signed-out visitors don't own the auto-provisioned workspace (chat
    // isolation), so waiting for a project here stranded them on
    // "Setting up your workspace…" with no Clerk card. Hold the index until
    // they authenticate; ChatView then takes the same slot for onboarding.
    if (showAuthWelcome || mostRecentProject === null || startingRef.current) {
      return;
    }
    startingRef.current = true;
    void handleNewThread(scopeProjectRef(mostRecentProject.environmentId, mostRecentProject.id), {
      replace: true,
    }).catch(() => {
      startingRef.current = false;
      setStartState((state) => ({ ...state, failed: true }));
    });
  }, [handleNewThread, mostRecentProject, showAuthWelcome, startState.retryRequest]);

  if (showAuthWelcome) {
    return <AuthWelcomeLanding />;
  }
  if (!bootstrapped) {
    return null;
  }
  if (mostRecentProject !== null) {
    return startState.failed ? (
      <DraftStartError
        onRetry={() => {
          setStartState((state) => ({
            failed: false,
            retryRequest: state.retryRequest + 1,
          }));
        }}
      />
    ) : null;
  }
  return <NoProjectsHero />;
}

function DraftStartError({ onRetry }: { readonly onRetry: () => void }) {
  return (
    <SidebarInset className="h-dvh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
      <Empty className="flex-1">
        <EmptyHeader className="max-w-md">
          <EmptyTitle className="text-foreground text-xl">Couldn’t start a new thread</EmptyTitle>
          <EmptyDescription className="mt-2 text-sm text-muted-foreground/78">
            The project is still available. Try opening the draft again.
          </EmptyDescription>
          <div className="mt-5 flex justify-center">
            <Button size="sm" onClick={onRetry}>
              <RotateCcwIcon className="size-4" />
              Try again
            </Button>
          </div>
        </EmptyHeader>
      </Empty>
    </SidebarInset>
  );
}

/**
 * Signed-out front door. This IS the real chat thread view: we mint a local
 * draft session on the always-present primary environment and render the actual
 * `ChatView`, so its draft-hero shows Morrow's branded greeting-with-sign-in
 * (`MorrowAuthWelcome`) with the real composer below it. ChatView already gates
 * sending while signed-out — an unsigned send opens Clerk and preserves the
 * drafted message — so every path here funnels into the same sign-in, after
 * which ChatView flips straight into onboarding (account type → form → tools →
 * greeting → suggested tasks).
 *
 * A synthetic `projectId` is fine: nothing validates it against the server
 * project list before the first send, and the draft never touches the backend
 * while the visitor is signed out.
 */
function AuthWelcomeLanding() {
  const primaryEnvironmentId = useAtomValue(primaryEnvironmentIdAtom);
  const setProjectDraftThreadId = useComposerDraftStore((store) => store.setProjectDraftThreadId);
  const [draft, setDraft] = useState<{
    draftId: ReturnType<typeof newDraftId>;
    threadId: ReturnType<typeof newThreadId>;
  } | null>(null);
  const mintedRef = useRef(false);

  useEffect(() => {
    if (!primaryEnvironmentId || mintedRef.current) {
      return;
    }
    mintedRef.current = true;
    const draftId = newDraftId();
    const threadId = newThreadId();
    setProjectDraftThreadId(scopeProjectRef(primaryEnvironmentId, newProjectId()), draftId, {
      threadId,
    });
    setDraft({ draftId, threadId });
  }, [primaryEnvironmentId, setProjectDraftThreadId]);

  return (
    <SidebarInset className="h-svh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground md:h-dvh">
      {primaryEnvironmentId && draft ? (
        <ChatView
          draftId={draft.draftId}
          environmentId={primaryEnvironmentId}
          threadId={draft.threadId}
          routeKind="draft"
          forceExpandedMobileComposer
        />
      ) : (
        // No primary environment yet (rare cold-boot window) — hold the slot
        // with just the greeting until the draft can be minted.
        <div className="relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-background">
          <div className="flex flex-1 items-center justify-center overflow-y-auto px-4 py-6">
            <MorrowAuthWelcome />
          </div>
        </div>
      )}
    </SidebarInset>
  );
}

function NoProjectsHero() {
  // MorrowOS provisions a single local workspace automatically on the server, so
  // this only appears for the brief cold-boot window before the workspace thread
  // is ready. No "add a project" action is shown.
  return (
    <SidebarInset className="h-dvh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden bg-background">
        <Empty className="flex-1">
          <div className="w-full max-w-lg px-8 py-12">
            <EmptyHeader className="max-w-none">
              <EmptyTitle className="text-foreground text-2xl sm:text-3xl">
                Setting up your workspace…
              </EmptyTitle>
              <EmptyDescription className="mt-2 text-sm text-muted-foreground/78">
                One moment — getting things ready.
              </EmptyDescription>
            </EmptyHeader>
          </div>
        </Empty>
      </div>
    </SidebarInset>
  );
}

export const Route = createFileRoute("/_chat/")({
  component: ChatIndexRouteView,
});

function HostedStaticOnboardingState() {
  const cloudEnabled = hasCloudPublicConfig();

  return (
    <SidebarInset className="h-dvh min-h-0 overflow-hidden overscroll-y-none bg-background text-foreground">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden bg-background">
        <header
          className={cn(
            "flex h-[var(--workspace-topbar-height)] min-h-[var(--workspace-topbar-height)] shrink-0 items-center border-b border-border px-3 transition-[padding-left] duration-200 ease-linear motion-reduce:transition-none sm:px-5",
            COLLAPSED_SIDEBAR_TITLEBAR_INSET_CLASS,
          )}
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-foreground md:text-muted-foreground/60">
              {APP_DISPLAY_NAME}
            </span>
          </div>
        </header>

        <Empty className="flex-1">
          <div className="w-full max-w-xl rounded-3xl border border-border/55 bg-card/20 px-8 py-12 shadow-sm/5">
            <EmptyHeader className="max-w-none">
              <div className="mx-auto mb-5 flex size-11 items-center justify-center rounded-xl border border-border/70 bg-background/70 text-muted-foreground">
                <LinkIcon className="size-5" />
              </div>
              <EmptyTitle className="text-foreground text-xl">
                Connect an environment to get started
              </EmptyTitle>
              <EmptyDescription className="mt-2 text-sm leading-relaxed text-muted-foreground/78">
                {cloudEnabled
                  ? "Sign in to T3 Connect to connect a linked environment through its managed tunnel, or add a reachable backend manually."
                  : "Add a reachable backend manually to start working from this browser."}
              </EmptyDescription>
              <div className="mt-6 flex justify-center">
                <Button render={<Link to="/settings/connections" />} size="sm">
                  <PlusIcon className="size-4" />
                  {cloudEnabled ? "Open Connections" : "Add environment"}
                </Button>
              </div>
            </EmptyHeader>
          </div>
        </Empty>
      </div>
    </SidebarInset>
  );
}
